import { Box, Flex, Text } from '@chakra-ui/react';
import type { StatusVerificationSection } from '@diamond/shared/features/registration/browser';
import { buildHomepageNooCorrectionUrl } from '@diamond/sol-buyer/utils';
import NextLink from 'next/link';
import { type KeyboardEvent } from 'react';

import { CorrectionArrowIcon, SectionKindIcon } from './NooRegistrationStatusIcons';
import { StatusBadge } from './NooRegistrationStatusSharedUi';

interface VerificationSectionCardProps {
  section: StatusVerificationSection;
  registrationToken: string;
  compact?: boolean;
  testIdSuffix?: string;
  desktop?: boolean;
  selected?: boolean;
  onSelect?: () => void;
  detailHref?: string;
}

export function VerificationSectionCard({
  section,
  registrationToken,
  compact = true,
  testIdSuffix = '',
  desktop = false,
  selected = false,
  onSelect,
  detailHref,
}: VerificationSectionCardProps) {
  const canOpenCorrection = Boolean(
    section.state === 'correction' && section.resumeStep && registrationToken
  );
  const cardPadding = compact ? 2 : 4;
  const isSelectable = Boolean(onSelect);
  const cardBorderColor = selected ? '#0941A8' : '#0000001A';
  const cardBackground = '#FFFFFF';
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (!onSelect || (event.key !== 'Enter' && event.key !== ' ')) {
      return;
    }

    event.preventDefault();
    onSelect();
  };

  const content = (
    <Flex align="start" gap={4}>
      <Flex
        w="48px"
        h="48px"
        shrink={0}
        align="center"
        justify="center"
        borderRadius="8px"
        bg="#F1F8FF"
      >
        <SectionKindIcon kind={section.kind} />
      </Flex>

      <Box flex={1} minW={0}>
        {section.eyebrow ? (
          <Text
            fontSize="xs"
            fontWeight="medium"
            color="#AEAEB2"
            lineHeight="20px"
          >
            {section.eyebrow}
          </Text>
        ) : null}
        <Text
          fontSize="lg"
          fontWeight="medium"
          lineHeight="27px"
          color="#373F41"
          whiteSpace="pre-line"
        >
          {section.title}
        </Text>
        <Text
          mt={section.eyebrow ? 0 : 1}
          fontSize="sm"
          lineHeight="20px"
          color="#666666"
        >
          {section.description}
        </Text>
      </Box>

      <Flex
        w="91px"
        shrink={0}
        direction="column"
        align="flex-end"
        justify="space-between"
        alignSelf="stretch"
        minH={canOpenCorrection ? '88px' : undefined}
      >
        <StatusBadge state={section.state} />
        {canOpenCorrection ? <CorrectionArrowIcon /> : null}
      </Flex>
    </Flex>
  );

  if (canOpenCorrection && section.resumeStep) {
    const correctionUrl = buildHomepageNooCorrectionUrl({
      step: section.resumeStep,
      registrationToken,
      outletIndex: section.outletIndex,
    });

    return (
      <Box
        as="a"
        href={correctionUrl}
        border="1px solid"
        borderColor={cardBorderColor}
        borderRadius="10px"
        bg={cardBackground}
        p={desktop ? 2 : cardPadding}
        display="block"
        _hover={{ bg: '#FAFAFA' }}
        onClick={onSelect}
        onKeyDown={handleKeyDown}
        aria-pressed={isSelectable ? selected : undefined}
        data-testid={`noo-buyer-status-section-${section.id}${testIdSuffix}`}
      >
        {content}
      </Box>
    );
  }

  if (isSelectable) {
    return (
      <Box
        as="button"
        type="button"
        w="full"
        textAlign="left"
        border="1px solid"
        borderColor={cardBorderColor}
        borderRadius="10px"
        bg={cardBackground}
        p={desktop ? 2 : cardPadding}
        _hover={{ bg: '#FAFAFA' }}
        onClick={onSelect}
        onKeyDown={handleKeyDown}
        aria-pressed={selected}
        data-testid={`noo-buyer-status-section-${section.id}${testIdSuffix}`}
      >
        {content}
      </Box>
    );
  }

  if (detailHref) {
    return (
      <Box
        as={NextLink}
        href={detailHref}
        display="block"
        border="1px solid"
        borderColor={cardBorderColor}
        borderRadius="10px"
        bg={cardBackground}
        p={desktop ? 2 : cardPadding}
        _hover={{ bg: '#FAFAFA' }}
        data-testid={`noo-buyer-status-section-${section.id}${testIdSuffix}`}
      >
        {content}
      </Box>
    );
  }

  return (
    <Box
      border="1px solid"
      borderColor={cardBorderColor}
      borderRadius="10px"
      bg={cardBackground}
      p={desktop ? 2 : cardPadding}
      data-testid={`noo-buyer-status-section-${section.id}${testIdSuffix}`}
    >
      {content}
    </Box>
  );
}
